import { useEffect, useState } from "react";
import { axiosInstance } from "../../../utils/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import DragDropUpload from "../../../components/DragDropUpload";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";

export default function BrandForm({ brandID, updateData }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: "",
    order: "",
    logo: null,
    swiss_product_code: "",
    international_product_code: "",
    toner_swiss_product_code: "",
    toner_international_product_code: "",
  });
  const [existingLogo, setExistingLogo] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (brandID) {
      const fetchBrandData = async () => {
        try {
          const response = await axiosInstance.get(
            `${window.REACT_APP_API_ENDPOINT}/device-brands/${brandID}`
          );
          const data = response.data;
          setFormData({
            name: data.name,
            order: data.order,
            logo: null,
            swiss_product_code:
              data.swiss_product_code === "————"
                ? ""
                : data.swiss_product_code || "",
            international_product_code:
              data.international_product_code === "————"
                ? ""
                : data.international_product_code || "",
            toner_swiss_product_code:
              data.toner_swiss_product_code === "————"
                ? ""
                : data.toner_swiss_product_code || "",
            toner_international_product_code:
              data.toner_international_product_code === "————"
                ? ""
                : data.toner_international_product_code || "",
          });
          setExistingLogo(data.logo);
        } catch (error) {
          console.error("Error fetching brand:", error);
        }
      };
      fetchBrandData();
    }
  }, [brandID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "swiss_product_code" || name === "toner_swiss_product_code") {
      // Force uppercase, only alphanumeric, must start with CH
      let cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
      if (cleaned && !cleaned.startsWith("CH")) {
        cleaned = "CH" + cleaned.replace(/^C?H?/i, "");
      }
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
    } else if (name === "international_product_code" || name === "toner_international_product_code") {
      // Force uppercase, only alphanumeric, must start with EX
      let cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
      if (cleaned && !cleaned.startsWith("EX")) {
        cleaned = "EX" + cleaned.replace(/^E?X?/i, "");
      }
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const optimizeImage = async (file) => {
    try {
      const options = {
        maxSizeMB: 0.15, // 150KB
        maxWidthOrHeight: 1024,
        useWebWorker: true,
        fileType: "image/png",
      };

      const compressedFile = await imageCompression(file, options);
      return new File(
        [compressedFile],
        file.name.replace(/\.[^/.]+$/, ".png"),
        {
          type: "image/png",
        }
      );
    } catch (error) {
      console.error("Error optimizing image:", error);
      throw error;
    }
  };

  const handleFileChange = async (file) => {
    if (file) {
      try {
        const optimizedFile = await optimizeImage(file);
        setFormData((prev) => ({ ...prev, logo: optimizedFile }));
        const url = URL.createObjectURL(optimizedFile);
        setPreviewUrl(url);
        if (errors.logo) {
          setErrors((prev) => ({ ...prev, logo: null }));
        }
      } catch (error) {
        toast.error("Error optimizing image. Please try again.");
        console.error("Image optimization error:", error);
      }
    }
  };

  const handleClearLogo = () => {
    setFormData((prev) => ({ ...prev, logo: null }));
    setPreviewUrl("");
    setExistingLogo("");
    if (errors.logo) {
      setErrors((prev) => ({ ...prev, logo: null }));
    }
  };

  const mutation = useMutation({
    mutationFn: (formDataToSend) => {
      if (brandID) {
        return axiosInstance.post(
          `${window.REACT_APP_API_ENDPOINT}/device-brands/update/${brandID}`,
          formDataToSend
        );
      } else {
        return axiosInstance.post(
          `${window.REACT_APP_API_ENDPOINT}/device-brands`,
          formDataToSend
        );
      }
    },
    onSuccess: () => {
      toast.success("Brand saved successfully");
      updateData();
      queryClient.invalidateQueries({ queryKey: ["brands"] });
    },
    onError: (error) => {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        console.error("Brand save error:", error);
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.name) {
      newErrors.name = "The name field is required.";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    if (!formData.order) {
      newErrors.order = "Brand Order is required";
    }
    if (!formData.logo && !brandID) {
      newErrors.logo = "The logo field is required.";
    }
    if (formData.swiss_product_code && !/^CH\d+$/.test(formData.swiss_product_code)) {
      newErrors.swiss_product_code = "Swiss product code must start with 'CH' followed by numbers (e.g. CH01)";
    }
    if (formData.international_product_code && !/^EX\d+$/.test(formData.international_product_code)) {
      newErrors.international_product_code = "International product code must start with 'EX' followed by numbers (e.g. EX01)";
    }
    if (formData.toner_swiss_product_code && !/^CH\d+$/.test(formData.toner_swiss_product_code)) {
      newErrors.toner_swiss_product_code = "Toner Swiss product code must start with 'CH' followed by numbers (e.g. CH01)";
    }
    if (formData.toner_international_product_code && !/^EX\d+$/.test(formData.toner_international_product_code)) {
      newErrors.toner_international_product_code = "Toner International product code must start with 'EX' followed by numbers (e.g. EX01)";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("order", formData.order);
    if (formData.logo) {
      formDataToSend.append("logo", formData.logo);
    }
    if (formData.swiss_product_code) {
      formDataToSend.append("swiss_product_code", formData.swiss_product_code);
    }
    if (formData.international_product_code) {
      formDataToSend.append("international_product_code", formData.international_product_code);
    }
    if (formData.toner_swiss_product_code) {
      formDataToSend.append("toner_swiss_product_code", formData.toner_swiss_product_code);
    }
    if (formData.toner_international_product_code) {
      formDataToSend.append("toner_international_product_code", formData.toner_international_product_code);
    }

    mutation.mutate(formDataToSend);
  };

  const inputClass = "block w-full rounded-md border-0 py-3.5 pl-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm transition-all hover:ring-gray-400";

  return (
    <div className="pt-4">
      <form
        className="w-full"
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        {/* Name */}
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-medium text-gray-900">
            Brand Name
          </label>
          <div className="relative mt-2">
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Brand Name"
              autoComplete="name"
              value={formData.name}
              onChange={handleInputChange}
              className={inputClass}
            />
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>
        </div>

        {/* Order */}
        <div className="mb-4">
          <label htmlFor="order" className="block text-sm font-medium text-gray-900">
            Brand Order
          </label>
          <div className="relative mt-2">
            <input
              id="order"
              name="order"
              type="text"
              placeholder="Brand Order"
              autoComplete="order"
              value={formData.order}
              onChange={handleInputChange}
              className={inputClass}
            />
            {errors.order && <p className="text-sm text-red-600">{errors.order}</p>}
          </div>
        </div>

        {/* Swiss Product Code */}
        <div className="mb-4">
          <label htmlFor="swiss_product_code" className="block text-sm font-medium text-gray-900">
            Swiss Product Code
          </label>
          <div className="relative mt-2">
            <div className="flex">
              <div className="flex items-center px-3 border border-r-0 rounded-l-md bg-red-50 border-red-300 pointer-events-none">
                <span className="text-red-500 text-sm font-medium">CH</span>
              </div>
              <input
                id="swiss_product_code"
                name="swiss_product_code"
                type="text"
                placeholder="01"
                value={formData.swiss_product_code.replace(/^CH/, "")}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, "");
                  setFormData((prev) => ({
                    ...prev,
                    swiss_product_code: digits ? "CH" + digits : "",
                  }));
                  if (errors.swiss_product_code) {
                    setErrors((prev) => ({ ...prev, swiss_product_code: null }));
                  }
                }}
                className="block w-full rounded-r-md border-0 py-3.5 pl-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm transition-all hover:ring-gray-400"
              />
            </div>
            {errors.swiss_product_code && (
              <p className="text-sm text-red-600 mt-1">{errors.swiss_product_code}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">Must start with 'CH' followed by numbers (e.g. CH01)</p>
          </div>
        </div>

        {/* International Product Code */}
        <div className="mb-4">
          <label htmlFor="international_product_code" className="block text-sm font-medium text-gray-900">
            International Product Code
          </label>
          <div className="relative mt-2">
            <div className="flex">
              <div className="flex items-center px-3 border border-r-0 rounded-l-md bg-blue-50 border-blue-300 pointer-events-none">
                <span className="text-blue-500 text-sm font-medium">EX</span>
              </div>
              <input
                id="international_product_code"
                name="international_product_code"
                type="text"
                placeholder="01"
                value={formData.international_product_code.replace(/^EX/, "")}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, "");
                  setFormData((prev) => ({
                    ...prev,
                    international_product_code: digits ? "EX" + digits : "",
                  }));
                  if (errors.international_product_code) {
                    setErrors((prev) => ({ ...prev, international_product_code: null }));
                  }
                }}
                className="block w-full rounded-r-md border-0 py-3.5 pl-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm transition-all hover:ring-gray-400"
              />
            </div>
            {errors.international_product_code && (
              <p className="text-sm text-red-600 mt-1">{errors.international_product_code}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">Must start with 'EX' followed by numbers (e.g. EX01)</p>
          </div>
        </div>

        {/* Toner Swiss Product Code */}
        <div className="mb-4">
          <label htmlFor="toner_swiss_product_code" className="block text-sm font-medium text-gray-900">
            Toner Swiss Product Code
          </label>
          <div className="relative mt-2">
            <div className="flex">
              <div className="flex items-center px-3 border border-r-0 rounded-l-md bg-red-50 border-red-300 pointer-events-none">
                <span className="text-red-500 text-sm font-medium">CH</span>
              </div>
              <input
                id="toner_swiss_product_code"
                name="toner_swiss_product_code"
                type="text"
                placeholder="01"
                value={formData.toner_swiss_product_code.replace(/^CH/, "")}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, "");
                  setFormData((prev) => ({
                    ...prev,
                    toner_swiss_product_code: digits ? "CH" + digits : "",
                  }));
                  if (errors.toner_swiss_product_code) {
                    setErrors((prev) => ({ ...prev, toner_swiss_product_code: null }));
                  }
                }}
                className="block w-full rounded-r-md border-0 py-3.5 pl-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm transition-all hover:ring-gray-400"
              />
            </div>
            {errors.toner_swiss_product_code && (
              <p className="text-sm text-red-600 mt-1">{errors.toner_swiss_product_code}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">Must start with 'CH' followed by numbers (e.g. CH01)</p>
          </div>
        </div>

        {/* Toner International Product Code */}
        <div className="mb-4">
          <label htmlFor="toner_international_product_code" className="block text-sm font-medium text-gray-900">
            Toner International Product Code
          </label>
          <div className="relative mt-2">
            <div className="flex">
              <div className="flex items-center px-3 border border-r-0 rounded-l-md bg-blue-50 border-blue-300 pointer-events-none">
                <span className="text-blue-500 text-sm font-medium">EX</span>
              </div>
              <input
                id="toner_international_product_code"
                name="toner_international_product_code"
                type="text"
                placeholder="01"
                value={formData.toner_international_product_code.replace(/^EX/, "")}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^0-9]/g, "");
                  setFormData((prev) => ({
                    ...prev,
                    toner_international_product_code: digits ? "EX" + digits : "",
                  }));
                  if (errors.toner_international_product_code) {
                    setErrors((prev) => ({ ...prev, toner_international_product_code: null }));
                  }
                }}
                className="block w-full rounded-r-md border-0 py-3.5 pl-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm transition-all hover:ring-gray-400"
              />
            </div>
            {errors.toner_international_product_code && (
              <p className="text-sm text-red-600 mt-1">{errors.toner_international_product_code}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">Must start with 'EX' followed by numbers (e.g. EX01)</p>
          </div>
        </div>

        {/* Logo */}
        <div className="col-span-full mb-4">
          <label htmlFor="logo" className="block text-sm font-medium text-gray-900">
            Brand Logo
          </label>
          <div className="mt-2">
            {existingLogo && !previewUrl ? (
              <div className="flex justify-center rounded-lg border border-dashed border-gray-300 px-6 py-10 bg-white">
                <div className="text-center">
                  <img
                    src={`${window.REACT_APP_API_ENDPOINT.replace(/\/api(?!.*\/api)/, "")}${existingLogo}`}
                    alt="Existing Brand Logo"
                    className="mx-auto h-12 w-12 object-contain"
                  />
                  <p className="text-sm text-gray-600 mt-2">Current logo</p>
                  <button
                    type="button"
                    onClick={handleClearLogo}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Replace logo
                  </button>
                </div>
              </div>
            ) : (
              <DragDropUpload
                onFileSelect={handleFileChange}
                accept="image/png, image/jpeg, image/jpg, image/gif"
                maxSize={5 * 1024 * 1024}
                showPreview={true}
                hasFile={!!formData.logo}
                className="bg-white"
              >
                <div className="text-center flex flex-col items-center justify-center py-8">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="mx-auto h-20 w-20 object-contain"
                    />
                  ) : (
                    <CloudArrowUpIcon className="text-gray-600 h-12 w-12 object-contain" />
                  )}
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-blue-600">Click to upload</span>{" "}
                    or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, GIF (will be converted to PNG, max 150KB)
                  </p>
                </div>
              </DragDropUpload>
            )}
            {errors.logo && <p className="text-sm text-red-600 mt-1">{errors.logo}</p>}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline focus:ring-2 focus:ring-indigo-600"
          >
            {mutation.isLoading ? (
              <svg
                className="animate-spin ml-2 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            ) : brandID ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}